import React, { useEffect, useMemo, useRef, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import LoginForm from '../components/LoginForm';
import AirbnbCalendar from '../components/AirbnbCalendar';
import useIcalUnavailableRanges from '../hooks/useIcalUnavailableRanges';
import i18n from '../i18n';
import { clearAuth, getAuthRole, setAuthRole, subscribeAuthRole } from '../utils/auth';
import { cleaningPackageForValue } from '../utils/cleaningPackages';
import './GestionePulizie.css';

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const toYmd = (date) => {
  const d = normalizeDate(date);
  return d.toISOString().slice(0, 10);
};

const reservationKeyForRange = (range) => {
  if (!range?.start || !range?.end) {
    return '';
  }
  return `${toYmd(range.start)}_${toYmd(range.end)}`;
};

const diffDaysUtc = (startDate, endDate) => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((utcEnd - utcStart) / (1000 * 60 * 60 * 24));
};

const msPerDay = 24 * 60 * 60 * 1000;

const parseYmdToDate = (ymd) => {
  if (typeof ymd !== 'string' || !ymd) {
    return null;
  }
  // Évite les soucis de fuseau horaire.
  const date = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Construit une Date telle que toYmd(date) === ymd, quel que soit le fuseau.
// Indispensable pour matérialiser un overlay : la ligne doit ré-encoder vers sa
// clé JSON exacte (sinon la recherche de l'overlay échoue à cause du décalage
// UTC de toYmd).
const dateForKeyPart = (ymd) => {
  const base = parseYmdToDate(ymd);
  if (!base) {
    return null;
  }
  let d = normalizeDate(base);
  if (toYmd(d) !== ymd) {
    d = normalizeDate(new Date(base.getTime() + msPerDay));
    if (toYmd(d) !== ymd) {
      d = normalizeDate(new Date(base.getTime() - msPerDay));
    }
  }
  return d;
};

const dayDiff = (a, b) => Math.round((normalizeDate(a) - normalizeDate(b)) / msPerDay);

// Deux périodes se chevauchent (bornes demi-ouvertes [start, end[ : un départ et
// une arrivée le même jour NE se chevauchent pas).
const rangesOverlap = (a, b) =>
  normalizeDate(a.start) < normalizeDate(b.end) && normalizeDate(b.start) < normalizeDate(a.end);

// Un overlay « vide » (aucun nom, aucun montant, aucun commentaire, non manuel)
// ne doit pas encombrer la liste.
const overlayHasContent = (overlay) => {
  if (!overlay || typeof overlay !== 'object') {
    return false;
  }
  if (typeof overlay.name === 'string' && overlay.name.trim()) {
    return true;
  }
  if (Number(overlay.totalNet) > 0 || Number(overlay.totalGross) > 0) {
    return true;
  }
  if (Array.isArray(overlay.comments) && overlay.comments.length > 0) {
    return true;
  }
  return Boolean(overlay.manualSlot);
};

const overlayRichness = (overlay) => {
  let score = 0;
  if (typeof overlay?.name === 'string' && overlay.name.trim()) {
    score += 10;
  }
  for (const field of ['totalNet', 'totalGross', 'commission', 'taxPaid', 'pricePerNight']) {
    if (Number(overlay?.[field]) > 0) {
      score += 2;
    }
  }
  if (Array.isArray(overlay?.comments)) {
    score += overlay.comments.length * 3;
  }
  return score;
};

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
});

const monthFormatter = new Intl.DateTimeFormat('it-IT', {
  month: 'long',
  year: 'numeric',
});

const PASSWORD_ROLES = {
  enzo10: 'admin',
  Gianni58: 'admin',
  cleaningRita: 'cleaning'
};

const GestionePulizie = () => {
  const [authRole, setAuthRoleState] = useState(() => getAuthRole());

  const isAllowed = authRole === 'admin' || authRole === 'cleaning';

  useEffect(() => {
    i18n.changeLanguage('it');
  }, []);

  useEffect(() => {
    return subscribeAuthRole(setAuthRoleState);
  }, []);

  const handleLogin = (role) => {
    if (role !== 'admin' && role !== 'cleaning') {
      return;
    }

    setAuthRole(role);
  };

  const handleLogout = () => {
    clearAuth();
  };

  const { ranges, isLoading, error } = useIcalUnavailableRanges({
    withSources: false,
    enabled: isAllowed,
  });

  const [overlays, setOverlays] = useState({});
  const [isLoadingOverlays, setIsLoadingOverlays] = useState(false);
  const [overlaysError, setOverlaysError] = useState('');
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentStatus, setCommentStatus] = useState({});

  useEffect(() => {
    if (!isAllowed) {
      return;
    }

    let isCancelled = false;

    const load = async () => {
      try {
        setIsLoadingOverlays(true);
        setOverlaysError('');

        const response = await fetch('/api/bookings/overlays.php', { cache: 'no-store' });
        const payload = await response.json();
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error || 'Impossibile caricare i dati.');
        }

        const loaded = payload?.data?.overlays || {};
        if (!isCancelled) {
          setOverlays(loaded);
        }
      } catch (err) {
        if (!isCancelled) {
          setOverlaysError(err?.message || 'Errore di caricamento.');
          setOverlays({});
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingOverlays(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [isAllowed]);

  const visibleRanges = useMemo(() => {
    return (ranges || []).filter((range) => {
      // Blocages plateforme (buffer, fermeture manuelle...) sans client réel derrière :
      // pas un séjour à préparer.
      if (range.kind === 'blocked') {
        return false;
      }
      const key = reservationKeyForRange(range);
      const overlay = overlays[key];
      return !(overlay && overlay.bookingMode === 'errore');
    });
  }, [overlays, ranges]);

  // Liste complète : réservations du flux iCal + vieilles réservations que
  // Airbnb/Booking ne renvoient plus, matérialisées depuis les overlays (comme
  // sur Gestione Prenotazioni). La villa étant une seule unité, tout chevauchement
  // est un doublon/erreur : on l'écarte (séjours courts et remplis en premier).
  const sortedReservations = useMemo(() => {
    const today = normalizeDate(new Date());
    const map = new Map();
    const added = [];

    const registerRange = (key, range) => {
      map.set(key, range);
      added.push({ start: range.start, end: range.end });
    };

    // 1) Flux iCal = vérité.
    for (const range of visibleRanges || []) {
      if (!range?.start || !range?.end) {
        continue;
      }
      const normalized = { ...range, start: normalizeDate(range.start), end: normalizeDate(range.end) };
      const key = reservationKeyForRange(normalized);
      if (!key || map.has(key)) {
        continue;
      }
      if (added.some((r) => rangesOverlap(r, normalized))) {
        continue;
      }
      registerRange(key, normalized);
    }

    // 2) Overlays absents du flux : séjours passés que Airbnb/Booking ne
    // renvoient plus, MAIS AUSSI créneaux saisis à la main (ex. "Soggiorno
    // proprietario") qui n'apparaîtront jamais dans un flux iCal, qu'ils
    // soient passés ou à venir — pas de filtre sur la date ici, sinon un
    // séjour propriétaire futur reste invisible (cf. incident 2026-09-06).
    const overlayEntries = Object.entries(overlays || {})
      .filter(([, overlay]) => overlayHasContent(overlay) && overlay.bookingMode !== 'errore')
      .map(([key, overlay]) => {
        const [fromYmd, toYmdPart] = key.split('_');
        const start = dateForKeyPart(fromYmd);
        const end = dateForKeyPart(toYmdPart);
        return { key, overlay, start, end };
      })
      .filter((entry) => entry.start && entry.end)
      .sort((a, b) => {
        const durationA = dayDiff(a.end, a.start);
        const durationB = dayDiff(b.end, b.start);
        if (durationA !== durationB) {
          return durationA - durationB; // séjour le plus court d'abord
        }
        return overlayRichness(b.overlay) - overlayRichness(a.overlay); // puis le plus rempli
      });

    for (const { key, start, end } of overlayEntries) {
      if (map.has(key)) {
        continue;
      }
      if (added.some((r) => rangesOverlap(r, { start, end }))) {
        continue;
      }
      registerRange(key, { start, end });
    }

    return Array.from(map.values())
      .filter((range) => {
        const key = reservationKeyForRange(range);
        const overlay = overlays[key] || null;
        const name = overlay && typeof overlay.name === 'string' ? overlay.name.trim() : '';
        if (!name && range.start && normalizeDate(range.start).getTime() === today.getTime()) {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.start - b.start);
  }, [visibleRanges, overlays]);

  // Pagamenti alla signora delle pulizie : una riga per ogni soggiorno con un
  // forfait scelto — la pulizia (e quindi il pagamento) avviene all'uscita.
  const cleaningPayments = useMemo(() => {
    const today = normalizeDate(new Date());
    return sortedReservations
      .map((range) => {
        const key = reservationKeyForRange(range);
        const pkg = cleaningPackageForValue(overlays[key]?.cleaningPackage);
        if (!pkg) {
          return null;
        }
        const date = normalizeDate(range.end);
        return { key, date, start: normalizeDate(range.start), pkg, isDone: date <= today };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);
  }, [sortedReservations, overlays]);

  const cleaningPaymentsTotalDone = cleaningPayments
    .filter((p) => p.isDone)
    .reduce((sum, p) => sum + p.pkg.price, 0);
  const cleaningPaymentsTotalPlanned = cleaningPayments
    .filter((p) => !p.isDone)
    .reduce((sum, p) => sum + p.pkg.price, 0);

  // Raggruppamento per mese di uscita, con subtotale mensile.
  const cleaningPaymentsByMonth = useMemo(() => {
    const groups = new Map();
    for (const payment of cleaningPayments) {
      const key = `${payment.date.getFullYear()}-${String(payment.date.getMonth() + 1).padStart(2, '0')}`;
      const existing = groups.get(key) || {
        key,
        label: monthFormatter.format(payment.date),
        payments: [],
        total: 0,
      };
      existing.payments.push(payment);
      existing.total += payment.pkg.price;
      groups.set(key, existing);
    }
    return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
  }, [cleaningPayments]);

  const listRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  // Affiche/masque les flèches « Archivio » / « Prossime » selon la position du scroll.
  const updateScrollIndicators = () => {
    const el = listRef.current;
    if (!el) {
      return;
    }
    setCanScrollUp(el.scrollTop > 8);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
  };

  const scrollList = (direction) => {
    const el = listRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ top: direction * Math.round(el.clientHeight * 0.8), behavior: 'smooth' });
  };

  // Positionne la liste (scrollable) sur la réservation en cours / prochaine :
  // les séjours passés restent au-dessus du scroll. Plusieurs passes (paint,
  // délais, chargement des polices) car les polices custom recalculent la mise
  // en page après coup et décaleraient un scroll trop précoce.
  useEffect(() => {
    if (isLoading || isLoadingOverlays) {
      return undefined;
    }
    let cancelled = false;
    const applyScroll = () => {
      if (cancelled) {
        return;
      }
      const container = listRef.current;
      if (!container) {
        return;
      }
      const cards = Array.from(container.querySelectorAll('li.pulizie-card'));
      const firstCard = cards[0];
      const target =
        cards.find((li) => !li.classList.contains('pulizie-card--past')) ||
        cards[cards.length - 1];
      if (!firstCard || !target) {
        return;
      }
      container.scrollTop = Math.max(0, target.offsetTop - firstCard.offsetTop);
      updateScrollIndicators();
    };

    const raf = window.requestAnimationFrame(applyScroll);
    const t1 = window.setTimeout(applyScroll, 250);
    const t2 = window.setTimeout(applyScroll, 700);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(applyScroll).catch(() => {});
    }

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isLoading, isLoadingOverlays, sortedReservations.length]);

  const formatBeds = (beds) => {
    if (!beds || typeof beds !== 'object') {
      return '';
    }

    const labels = [];
    if (beds.matrimoniale) labels.push('matrimoniale');
    if (beds.lettino1) labels.push('lettino 1');
    if (beds.lettino2) labels.push('lettino 2');
    if (beds.divanoLetto) labels.push('divano letto');
    if (beds.lettiniAssemblati) labels.push('lettini assemblati');

    return labels.length > 0 ? labels.join(', ') : '';
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const setDraft = (key, value) => {
    setCommentDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddComment = async (key, range) => {
    const text = (commentDrafts[key] || '').trim();
    if (!text || !key) {
      return;
    }

    const by = authRole === 'cleaning' ? 'Pulizie' : 'Proprietario';
    setCommentStatus((prev) => ({ ...prev, [key]: 'saving' }));

    try {
      const response = await fetch('/api/bookings/overlays.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add_comment', reservationKey: key, text, by }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Errore durante il salvataggio.');
      }

      setOverlays((prev) => ({ ...prev, [key]: payload.data }));
      setDraft(key, '');
      setCommentStatus((prev) => ({ ...prev, [key]: 'saved' }));
      window.setTimeout(() => {
        setCommentStatus((prev) => ({ ...prev, [key]: '' }));
      }, 1500);

      const startLabel = range?.start ? formatDate(range.start) : '';
      const endLabel = range?.end ? formatDate(range.end) : '';
      // Même type que les commentaires admin (`reservation_comment`) pour réutiliser
      // le routage n8n → Telegram déjà en place ; le 🧹 et « 👤 Pulizie » identifient
      // qu'il vient de la femme de ménage. Envoyé APRÈS la sauvegarde réussie.
      fetch('https://n8n.atoneo.com/webhook/commentaire-site', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({
          type: 'reservation_comment',
          message: `🧹 *Nuovo commento pulizie*\n\n💬 ${text}\n\n📅 ${startLabel} → ${endLabel}\n👤 ${by}`,
        }),
      }).catch(() => {
        // Ne bloque pas l'UI si le webhook est indisponible.
      });
    } catch (err) {
      setCommentStatus((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  if (!isAllowed) {
    return (
      <LoginForm
        onLogin={handleLogin}
        passwordRoles={PASSWORD_ROLES}
        labels={{
          title: 'Gestione Pulizie',
          placeholder: 'Inserisci la password',
          submit: 'Accedi',
          errorInvalid: 'Password non corretta'
        }}
      />
    );
  }

  const today = normalizeDate(new Date());
  const nextReservation = sortedReservations.find((range) => normalizeDate(range.end) > today) || null;
  const nextKey = nextReservation ? reservationKeyForRange(nextReservation) : null;

  return (
    <div className="site-shell admin-shell">
      <AdminSidebar
        onLogout={handleLogout}
        sitePath="/it/affitto-villa-appartamento-sardegna-san-teodoro"
        role={authRole}
      />
      <main className="site-shell-content admin-content">
        <div className="admin-panel pulizie-panel">

          <h2>Gestione Pulizie</h2>

          {(isLoading || isLoadingOverlays) && <p className="pulizie-loading">Caricamento…</p>}
          {!isLoading && error && <p className="admin-error">{error}</p>}
          {!isLoadingOverlays && overlaysError && <p className="admin-error">{overlaysError}</p>}

          {!isLoading && !error && sortedReservations.length === 0 && (
            <p className="pulizie-empty">Nessuna prenotazione trovata.</p>
          )}

          {!isLoading && !error && sortedReservations.length > 0 && (
            <div className="pulizie-list-wrap">
              {canScrollUp && (
                <button
                  type="button"
                  className="pulizie-scroll-hint pulizie-scroll-hint--top"
                  onClick={() => scrollList(-1)}
                >
                  <span className="pulizie-scroll-hint-arrow pulizie-scroll-hint-arrow--up" aria-hidden="true">▲</span>
                  Archivio
                </button>
              )}
              <ul className="pulizie-list" ref={listRef} onScroll={updateScrollIndicators}>
              {sortedReservations.map((range, index) => {
                const nights = diffDaysUtc(range.start, range.end);
                const key = reservationKeyForRange(range);
                const overlay = overlays[key] || null;
                const adults = overlay && Number.isFinite(overlay.adults) ? overlay.adults : 0;
                const children = overlay && Number.isFinite(overlay.children) ? overlay.children : 0;
                const peopleTotal = adults + children;
                const bedsText = formatBeds(overlay?.beds);
                const cleaningPkg = cleaningPackageForValue(overlay?.cleaningPackage);
                const notes = overlay && typeof overlay.notes === 'string' ? overlay.notes.trim() : '';
                const comments = Array.isArray(overlay?.comments) ? overlay.comments : [];
                const isNext = key === nextKey;
                const isPast = normalizeDate(range.end) <= today;

                const nextRange = sortedReservations[index + 1];
                // Alerte uniquement si le jour de rotation n'est pas déjà passé.
                const hasConflict =
                  nextRange &&
                  normalizeDate(range.end) >= today &&
                  normalizeDate(range.end).getTime() === normalizeDate(nextRange.start).getTime();

                return (
                  <React.Fragment
                    key={`${range.start?.toISOString?.() || index}-${range.end?.toISOString?.() || index}`}
                  >
                    <li
                      className={`pulizie-card${isNext ? ' pulizie-card--next' : ''}${isPast ? ' pulizie-card--past' : ''}`}
                    >
                    {isNext && <span className="pulizie-card-badge">Prossimo</span>}

                    <div className="pulizie-card-dates">
                      <div className="pulizie-card-date-block">
                        <span className="pulizie-card-date-label">Arrivo</span>
                        <span className="pulizie-card-date-value">{formatDate(range.start)}</span>
                      </div>
                      <span className="pulizie-card-sep">→</span>
                      <div className="pulizie-card-date-block">
                        <span className="pulizie-card-date-label">Partenza</span>
                        <span className="pulizie-card-date-value">{formatDate(range.end)}</span>
                      </div>
                      <div className="pulizie-card-meta">
                        <span className="pulizie-chip">{nights} notti</span>
                        {peopleTotal > 0 && <span className="pulizie-chip">{peopleTotal} pers.</span>}
                        {notes && (
                          <span className="pulizie-note-dot" title={notes} aria-label="Note présentes">
                            📋
                          </span>
                        )}
                      </div>
                    </div>

                    {cleaningPkg && (
                      <div className={`pulizie-forfait pulizie-forfait--${cleaningPkg.value}`}>
                        <span className="pulizie-forfait-icon" aria-hidden="true">🧺</span>
                        <span className="pulizie-forfait-tag">Uscita</span>
                        <span className="pulizie-forfait-label">{cleaningPkg.label}</span>
                        <span className="pulizie-forfait-price">{cleaningPkg.price} €</span>
                      </div>
                    )}

                    {(adults > 0 || children > 0) && (
                      <div className="pulizie-card-people">
                        {adults > 0 && <span className="pulizie-chip pulizie-chip--info">{adults} adulti</span>}
                        {children > 0 && <span className="pulizie-chip pulizie-chip--info">{children} bambini</span>}
                      </div>
                    )}

                    {bedsText ? (
                      <div className="pulizie-card-row">
                        <span className="pulizie-card-row-label">Letti da preparare</span>
                        <span className="pulizie-card-row-value">{bedsText}</span>
                      </div>
                    ) : null}

                    {notes ? (
                      <div className="pulizie-card-row pulizie-card-row--notes">
                        <span className="pulizie-card-row-label">Note</span>
                        <span className="pulizie-card-row-value">{notes}</span>
                      </div>
                    ) : null}

                    <div className="pulizie-card-comments">
                      <span className="pulizie-card-row-label">Commenti</span>
                      {comments.length > 0 ? (
                        <ul className="pulizie-comments-list">
                          {comments.map((c, ci) => (
                            <li key={`${c.at || ci}-${ci}`} className="pulizie-comment">
                              <span className="pulizie-comment-text">{c.text}</span>
                              <span className="pulizie-comment-meta">
                                {c.by ? `${c.by} · ` : ''}
                                {c.at ? new Date(c.at).toLocaleDateString('it-IT') : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="pulizie-comments-empty">Nessun commento</span>
                      )}
                      <div className="pulizie-comment-add">
                        <input
                          type="text"
                          className="pulizie-comment-input"
                          placeholder="Aggiungi un commento…"
                          value={commentDrafts[key] || ''}
                          onChange={(e) => setDraft(key, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddComment(key, range);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="pulizie-comment-btn"
                          onClick={() => handleAddComment(key, range)}
                          disabled={commentStatus[key] === 'saving' || !(commentDrafts[key] || '').trim()}
                        >
                          {commentStatus[key] === 'saving' ? '…' : 'Invia'}
                        </button>
                        {commentStatus[key] === 'saved' && (
                          <span className="pulizie-comment-ok">✓</span>
                        )}
                        {commentStatus[key] === 'error' && (
                          <span className="pulizie-comment-error">Errore</span>
                        )}
                      </div>
                    </div>
                  </li>

                  {hasConflict && (
                    <li className="pulizie-conflict-alert" role="alert">
                      <span className="pulizie-conflict-alert-icon">⚠️</span>
                      <span>
                        Arrivo lo stesso giorno della partenza —{' '}
                        <strong>nessuna notte libera</strong> tra i due soggiorni
                      </span>
                    </li>
                  )}
                </React.Fragment>
                );
              })}
              </ul>
              {canScrollDown && (
                <button
                  type="button"
                  className="pulizie-scroll-hint pulizie-scroll-hint--bottom"
                  onClick={() => scrollList(1)}
                >
                  <span className="pulizie-scroll-hint-arrow pulizie-scroll-hint-arrow--down" aria-hidden="true">▼</span>
                  Prossime
                </button>
              )}
            </div>
          )}

          <details className="pulizie-calendar-details" open>
            <summary className="pulizie-calendar-summary">Calendario</summary>
            <div className="pulizie-calendar-body">
              <AirbnbCalendar
                lang="it"
                readOnly={true}
                showPrices={false}
                alternateReservedPeriodColors={true}
                withSources={false}
                showSourceBadges={false}
                unavailableRanges={visibleRanges}
                showOwnerStayBadge={false}
              />
            </div>
          </details>

          {/* ── Pagamenti pulizie (uscita) ───────────── */}
          {!isLoading && !isLoadingOverlays && cleaningPayments.length > 0 && (
            <div className="pulizie-payments">
              <h3>💶 Elenco Pagamenti pulizie</h3>
              <table className="pulizie-payments-table">
                <thead>
                  <tr>
                    <th>Data (uscita)</th>
                    <th>Forfait</th>
                    <th className="pulizie-payments-num">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {cleaningPaymentsByMonth.map((group) => (
                    <React.Fragment key={group.key}>
                      <tr className="pulizie-payments-month">
                        <th colSpan={3}>
                          {group.label}
                          <span className="pulizie-payments-month-total">
                            totale {currencyFormatter.format(group.total)}
                          </span>
                        </th>
                      </tr>
                      {group.payments.map((payment) => (
                        <tr
                          key={payment.key}
                          className={payment.isDone ? '' : 'pulizie-payments-row--future'}
                        >
                          <td>
                            <span className="pulizie-payments-entry">
                              {formatDate(payment.start)} →
                            </span>{' '}
                            {formatDate(payment.date)}
                          </td>
                          <td className={`pulizie-payments-forfait--${payment.pkg.value}`}>
                            {payment.pkg.label}
                            {!payment.isDone && (
                              <span className="pulizie-payments-badge">prevista</span>
                            )}
                          </td>
                          <td className={`pulizie-payments-num pulizie-payments-forfait--${payment.pkg.value}`}>
                            {currencyFormatter.format(payment.pkg.price)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={2}>Totale pulizie svolte</th>
                    <th className="pulizie-payments-num">
                      {currencyFormatter.format(cleaningPaymentsTotalDone)}
                    </th>
                  </tr>
                  {cleaningPaymentsTotalPlanned > 0 && (
                    <tr className="pulizie-payments-row--future">
                      <th colSpan={2}>Totale pulizie previste</th>
                      <th className="pulizie-payments-num">
                        {currencyFormatter.format(cleaningPaymentsTotalPlanned)}
                      </th>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default GestionePulizie;
